import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { useForm } from 'react-hook-form';
import api from '../../utils/api';
import { Save, ArrowRight } from 'lucide-react';

export default function EditListingPage() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const { data, isLoading } = useQuery(['listing', uuid], () =>
    api.get(`/listings/${uuid}`).then(r => r.data)
  );

  const { data: cats } = useQuery('categories', () =>
    api.get('/categories').then(r => r.data)
  );

  useEffect(() => {
    if (data?.listing) {
      const l = data.listing;
      reset({
        title: l.title,
        description: l.description,
        listing_type: l.listing_type,
        category_id: l.category_id,
        city: l.city,
        country: l.country,
        address: l.address,
        latitude: l.latitude,
        longitude: l.longitude,
        price_per_hour: l.price_per_hour,
        currency: l.currency,
        min_booking_hours: l.min_booking_hours,
        max_booking_hours: l.max_booking_hours,
        area: l.area,
        capacity: l.capacity,
        amenities: l.amenities,
        rules: l.rules,
        is_active: l.is_active,
      });
    }
  }, [data, reset]);

  const updateMutation = useMutation(
    (formData) => api.put(`/listings/${uuid}`, formData),
    { onSuccess: () => navigate('/dashboard/listings') }
  );

  if (isLoading) return <div className="skeleton h-96 rounded-2xl" />;

  return (
    <div className="max-w-2xl mx-auto" dir="rtl">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg bg-slate-700/50 text-slate-400 hover:text-white">
          <ArrowRight size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">تعديل الإعلان</h1>
          <p className="text-slate-400 text-sm">{data?.listing?.title}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(d => updateMutation.mutate(d))} className="space-y-6">
        <div className="glass rounded-2xl p-6 space-y-5">
          <h3 className="font-bold text-white border-b border-slate-700 pb-3">المعلومات الأساسية</h3>
          <div>
            <label className="block text-sm text-slate-400 mb-2">عنوان الإعلان *</label>
            <input {...register('title', { required: true })} className="form-input w-full" />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">الوصف *</label>
            <textarea {...register('description', { required: true })} rows={5} className="form-input w-full resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">الفئة</label>
              <select {...register('category_id')} className="form-input w-full">
                {cats?.categories?.map(c => <option key={c.id} value={c.id}>{c.name_ar}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">الحالة</label>
              <select {...register('is_active')} className="form-input w-full">
                <option value={1}>نشط</option>
                <option value={0}>متوقف</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">المرافق</label>
            <textarea {...register('amenities')} rows={3} className="form-input w-full resize-none" />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">الشروط</label>
            <textarea {...register('rules')} rows={3} className="form-input w-full resize-none" />
          </div>
        </div>

        <div className="glass rounded-2xl p-6 space-y-5">
          <h3 className="font-bold text-white border-b border-slate-700 pb-3">الموقع والسعر</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">المدينة *</label>
              <input {...register('city', { required: true })} className="form-input w-full" />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">الدولة</label>
              <input {...register('country')} className="form-input w-full" />
            </div>
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">العنوان</label>
            <input {...register('address')} className="form-input w-full" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">السعر / ساعة *</label>
              <input type="number" step="0.01" {...register('price_per_hour', { required: true })} className="form-input w-full" />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">العملة</label>
              <select {...register('currency')} className="form-input w-full">
                <option value="SAR">ريال سعودي</option>
                <option value="AED">درهم إماراتي</option>
                <option value="KWD">دينار كويتي</option>
                <option value="QAR">ريال قطري</option>
                <option value="USD">دولار أمريكي</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">الحد الأدنى (ساعات)</label>
              <input type="number" {...register('min_booking_hours')} className="form-input w-full" />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">الحد الأقصى (ساعات)</label>
              <input type="number" {...register('max_booking_hours')} className="form-input w-full" />
            </div>
          </div>
        </div>

        {updateMutation.isError && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
            حدث خطأ. يرجى المحاولة مرة أخرى.
          </div>
        )}

        <button type="submit" disabled={updateMutation.isLoading} className="w-full btn-primary flex items-center justify-center gap-2">
          <Save size={18} />
          {updateMutation.isLoading ? 'جارٍ الحفظ...' : 'حفظ التغييرات'}
        </button>
      </form>
    </div>
  );
}
